<script setup lang="ts">
import { computed } from 'vue';
import { i18n } from '@/i18n';
import TextButton from '@/components/input/Btn/TextButton.vue';

const t = i18n.t;

/**
 * EzTableのエラー状態表示コンポーネント
 * テーブルのデータ取得エラーなどを表示し、再試行機能を提供する
 */

/**
 * Props定義
 */
interface Props {
  /** エラーメッセージ */
  errorMessage?: string;
  /** 再試行ボタンを表示するか */
  showRetryButton?: boolean;
  /** カスタムCSSクラス（省略可） */
  customClass?: string;
}

/**
 * Events定義
 */
interface Emits {
  /** 再試行ボタンクリック時 */
  retry: [];
}

const props = withDefaults(defineProps<Props>(), {
  errorMessage: '',
  showRetryButton: true,
  customClass: '',
});

const emits = defineEmits<Emits>();

/**
 * 再試行ボタンクリックハンドラ
 */
const handleRetry = () => {
  emits('retry');
};

/**
 * 表示するエラーメッセージを取得
 */
const displayErrorMessage = computed(() => {
  return props.errorMessage || t('データの取得に失敗しました。再度お試しください。');
});
</script>

<template>
  <div :class="['ez-table-error-state', customClass]">
    <div class="ez-table-error-state__content">
      <div class="ez-table-error-state__icon">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="9" stroke="#e53935" stroke-width="2" />
          <path d="M15 9l-6 6M9 9l6 6" stroke="#e53935" stroke-width="2" stroke-linecap="round" />
        </svg>
      </div>

      <div class="ez-table-error-state__message">
        {{ displayErrorMessage }}
      </div>

      <TextButton v-if="showRetryButton" class="ez-table-error-state__retry-button" button-style="btn-primary" :on-click="handleRetry">
        {{ t('再取得') }}
      </TextButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.ez-table-error-state {
  display: grid;
  place-items: center;
  padding: 48px 24px;
  min-height: 300px;
  height: 100%;
  background-color: $semanticErrorSubdued;

  &__content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;
    max-width: 400px;
    text-align: center;
  }

  &__icon {
    opacity: 0.8;
  }

  &__message {
    font-size: 14px;
    color: #e53935;
    line-height: 1.5;
    margin: 0;
    font-weight: 400;
  }

  &__retry-button {
    min-width: 120px;
  }
}
</style>
